/**
 * In-memory cache for downscaled images.
 * Stores Blob Object URLs keyed by image URL and size.
 * Uses Blob URLs instead of base64 data URLs to keep image data
 * in browser-managed memory rather than the JS heap (~10x less RAM).
 * Implements LRU eviction with proper Blob URL revocation.
 */
class ImageCache {
  private cache: Map<string, string> = new Map();
  private readonly MAX_ENTRIES = 200;

  /**
   * Generates a cache key from image identifier and size.
   */
  private getCacheKey(imageUrl: string, size: string): string {
    return `${imageUrl}_${size}`;
  }

  /**
   * Retrieves a cached downscaled image.
   * Touches the entry to mark it as recently used (LRU).
   */
  get(imageUrl: string, size: string): string | undefined {
    const key = this.getCacheKey(imageUrl, size);
    const val = this.cache.get(key);
    // Touch to keep LRU order - move to end (most recent)
    if (val !== undefined) {
      this.cache.delete(key);
      this.cache.set(key, val);
    }
    return val;
  }

  /**
   * Stores a downscaled image in cache.
   * Evicts least recently used entry if cache exceeds MAX_ENTRIES.
   * Revokes the Blob URL of evicted entries to free browser memory.
   */
  set(imageUrl: string, size: string, blobUrl: string): void {
    const key = this.getCacheKey(imageUrl, size);

    const existing = this.cache.get(key);
    // Skip if the exact same blob URL is already cached (shared promise resolved for multiple components)
    if (existing === blobUrl) return;
    // Revoke old Blob URL only when genuinely replacing with a different one
    if (existing) {
      URL.revokeObjectURL(existing);
    }

    this.cache.set(key, blobUrl);
    if (this.cache.size > this.MAX_ENTRIES) {
      // Evict LRU (oldest entry - first in Map)
      const oldestKey = this.cache.keys().next().value;
      if (oldestKey) {
        const oldestVal = this.cache.get(oldestKey);
        if (oldestVal) URL.revokeObjectURL(oldestVal);
        this.cache.delete(oldestKey);
      }
    }
  }

  /**
   * Checks if an image is cached.
   */
  has(imageUrl: string, size: string): boolean {
    return this.cache.has(this.getCacheKey(imageUrl, size));
  }

  /**
   * Clears the entire cache, revoking all Blob URLs.
   */
  clear(): void {
    for (const blobUrl of this.cache.values()) {
      URL.revokeObjectURL(blobUrl);
    }
    this.cache.clear();
  }

  /**
   * Removes a specific cached image, revoking its Blob URL.
   */
  delete(imageUrl: string, size: string): void {
    const key = this.getCacheKey(imageUrl, size);
    const val = this.cache.get(key);
    if (val) URL.revokeObjectURL(val);
    this.cache.delete(key);
  }
}

// Export singleton instance
export const imageCache = new ImageCache();

/**
 * Negative cache for URLs that returned 404 or other fetch errors.
 * Prevents hammering the backend with repeated requests for images
 * that don't exist (e.g. deleted avatars, missing logos).
 * Keyed by raw image URL (not per-size — if the image is gone, no size will work).
 * Entries expire after FAILED_URL_TTL_MS so that if the image is later uploaded,
 * it will eventually be picked up without a full page reload.
 */
const FAILED_URL_TTL_MS = 5 * 60 * 1000; // 5 minutes
const failedUrlCache = new Map<string, number>(); // url → timestamp of failure

export const imageFailedUrls = {
  /** Mark a URL as failed. All sizes will skip fetching until TTL expires. */
  mark(url: string): void {
    failedUrlCache.set(url, Date.now());
  },
  /** Check if a URL is known-failed and still within TTL. */
  has(url: string): boolean {
    const timestamp = failedUrlCache.get(url);
    if (timestamp === undefined) return false;
    if (Date.now() - timestamp > FAILED_URL_TTL_MS) {
      failedUrlCache.delete(url);
      return false;
    }
    return true;
  },
  /** Clear all failed URL entries. */
  clear(): void {
    failedUrlCache.clear();
  },
};

/**
 * In-flight request deduplication map.
 * Keyed by original image URL (without size suffix).
 * Multiple avatar sizes sharing the same source URL will
 * reuse a single fetch, then each downscales independently.
 */
export const imageFetchInFlight = new Map<string, Promise<Blob>>();

/**
 * In-flight downscale deduplication map.
 * Keyed by `${url}_${size}` — deduplicates the per-size downscale step
 * so multiple avatars with the same URL+size share one canvas operation.
 */
export const imageDownscaleInFlight = new Map<string, Promise<string>>();
